import { angleAfterPositionChange } from "./watermarkAngle";
import { useState, useCallback } from "react";
import { ToolContainer, FileDropZone, ProcessingState, DownloadButton, downloadBlob, ResultPreview } from "@/components/ToolLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { stemOfPdfName } from "./pdfMetadata";
import { BatchRunner, BatchModeToggle } from "@/components/BatchRunner";
import { useSessionState } from "@/hooks/useSessionState";
import { PDFDocument } from "pdf-lib";
import { addTextWatermark, addImageWatermark, type WatermarkPosition } from "./operations";
import { needsPicture } from "./drawTextAnywhere";
import { watermarkTargetPages } from "./watermarkPages";
import { watermarkTextProblem } from "./watermarkText";

const TOOL_INFO_CONTENT = {
  what: `Stamps a text or image watermark across every page of a PDF, commonly used to mark a document as a draft or confidential, or to brand it with a logo before it's shared. It's a visual signal, not a security measure — the watermark makes the document's status or origin obvious to anyone looking at it, but doesn't prevent copying, editing, or redistribution the way password protection does.`,
  how: `Upload your PDF, choose Text or Image, then set the watermark content, opacity, size, colour, angle and position (or Tiled, to repeat it across the whole page); an image watermark also has a logo width. By default the watermark goes on every page; choose \"Skip the first page\" for a cover, or type a page range such as 2-5, 8. Text in the Latin alphabet is added as real, searchable text that stays sharp when printed; text in other scripts (Chinese, Arabic, Hindi...) is drawn as a picture so it displays correctly. By default the watermark sits on top of the page; choose \"Behind page content\" to keep body text fully dark (a full-page scan or a solid background hides a watermark placed behind it). Download the watermarked file when it looks right. To stamp several PDFs the same way, switch to Several files: up to 5 at a time on the trial, counted as one use, with the results as a ZIP.`,
  tip: `If the goal is actually restricting access rather than labeling a document's status, pair this with PDF Password, or use that instead — a watermark alone doesn't stop anyone from copying or editing the underlying content.`,
};

type Mode = "text" | "image";
const hexToRgb01 = (hex: string) => ({ r: parseInt(hex.slice(1, 3), 16) / 255, g: parseInt(hex.slice(3, 5), 16) / 255, b: parseInt(hex.slice(5, 7), 16) / 255 });
const POSITIONS: { value: WatermarkPosition; label: string }[] = [
  { value: "center", label: "Center" },
  { value: "top-left", label: "Top Left" },
  { value: "top-right", label: "Top Right" },
  { value: "bottom-left", label: "Bottom Left" },
  { value: "bottom-right", label: "Bottom Right" },
  { value: "tiled", label: "Tiled" },
];

export default function PdfWatermark() {
  const [file, setFile] = useState<File | null>(null);
  const [mode, setMode] = useSessionState<Mode>("wm-mode", "text");
  const [watermarkText, setWatermarkText] = useSessionState("wm-text", "CONFIDENTIAL");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [opacity, setOpacity] = useSessionState("wm-opacity", 40);
  const [position, setPosition] = useSessionState<WatermarkPosition>("wm-position", "center");
  const [size, setSize] = useSessionState("wm-size", 50);
  const [angle, setAngle] = useSessionState("wm-angle", 45);
  const [color, setColor] = useSessionState("wm-color", "#808080");
  const [logoScale, setLogoScale] = useSessionState("wm-logo", 30);
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState<Blob | null>(null);
  const [batchMode, setBatchMode] = useState(false);
  const [numPages, setNumPages] = useState(0);
  const [skipFirst, setSkipFirst] = useSessionState("wm-skip-first", false);
  const [pageRange, setPageRange] = useSessionState("wm-pages", "");
  const [layer, setLayer] = useSessionState<"above" | "behind">("wm-layer", "above");

  const handleFiles = useCallback((files: File[]) => {
    const f = files[0] || null;
    setFile(f); setResult(null); setNumPages(0);
    if (f) f.arrayBuffer().then((b) => PDFDocument.load(b, { ignoreEncryption: true, updateMetadata: false })).then((d) => setNumPages(d.getPageCount())).catch(() => {});
  }, []);

  /** The chosen pages for one file: a message when the choice does not fit, otherwise 0-based page numbers (undefined = all). */
  const pagesFor = (pageCount: number): { pages?: number[]; error?: string } => {
    if (!skipFirst && !pageRange.trim()) return {};
    const r = watermarkTargetPages({ skipFirst, range: pageRange }, pageCount);
    return "error" in r ? { error: r.error } : { pages: r.pages };
  };
  // Single file: the page count is known, so the message is live. Several files: only the typing is checked here.
  const pagesError = pagesFor(numPages > 0 && !batchMode ? numPages : 100000).error ?? null;

  /** A message when the settings are not usable yet. */
  const settingsProblem = (): string | null => {
    const wording = mode === "text" ? watermarkTextProblem(watermarkText) : null;
    if (wording) return wording;
    if (mode === "image" && !imageFile) return "Choose a logo image first";
    if (pagesError) return pagesError;
    return null;
  };
  const stamp = async (f: File): Promise<Blob> => {
    let pages: number[] | undefined;
    if (skipFirst || pageRange.trim()) {
      const count = (await PDFDocument.load(await f.arrayBuffer(), { ignoreEncryption: true, updateMetadata: false })).getPageCount();
      const chosen = pagesFor(count);
      if (chosen.error) throw new Error(`${f.name}: ${chosen.error}`);
      pages = chosen.pages;
    }
    return mode === "text"
      ? addTextWatermark(f, watermarkText, { opacity: opacity / 100, position, fontSize: size, rotationDegrees: angle, color: hexToRgb01(color), pages, layer })
      : addImageWatermark(f, imageFile!, { opacity: opacity / 100, position, rotationDegrees: angle, widthFraction: logoScale / 100, pages, layer });
  };

  const addWatermark = async () => {
    if (!file) return;
    const problem = settingsProblem();
    if (problem) { toast.error(problem); return; }
    setProcessing(true);
    try {
      const blob = await stamp(file);
      setResult(blob);
      toast.success("Watermark added!");
    } catch (err) {
      console.error("Watermark failed:", err);
      toast.error("Failed to add watermark");
    }
    finally { setProcessing(false); }
  };

  const settingsBlock = (
    <div className="space-y-4">
          <div className="flex gap-2">
            <Button size="sm" variant={mode === "text" ? "default" : "outline"} onClick={() => { setMode("text"); setResult(null); }}>Text</Button>
            <Button size="sm" variant={mode === "image" ? "default" : "outline"} onClick={() => { setMode("image"); setResult(null); }}>Image / Logo</Button>
          </div>

          {mode === "text" ? (
            <div>
              <Label htmlFor="wm-text">Watermark Text</Label>
              <Input id="wm-text" value={watermarkText} aria-invalid={!!watermarkTextProblem(watermarkText)} aria-describedby="wm-text-msg" onChange={e => { setWatermarkText(e.target.value); setResult(null); }} />
              <div id="wm-text-msg" aria-live="polite">{watermarkTextProblem(watermarkText) && <p role="alert" className="text-xs text-destructive mt-1">{watermarkTextProblem(watermarkText)}</p>}</div>
              {needsPicture(watermarkText) && <p className="text-xs text-muted-foreground mt-1">Some of these characters are drawn as a picture so they show correctly. That part can't be selected or searched. Latin-alphabet text is added as real text.</p>}
            </div>
          ) : (
            <div>
              <Label>Logo image (PNG, JPEG, WebP...)</Label>
              <Input aria-label="Logo image (PNG, JPEG, WebP...)"
                type="file"
                accept="image/*"
                onChange={e => { setImageFile(e.target.files?.[0] ?? null); setResult(null); }}
              />
              {imageFile ? <p className="text-xs text-muted-foreground mt-1">{imageFile.name}</p> : <p role="alert" className="text-xs text-destructive mt-1">Choose a logo image first</p>}
            </div>
          )}

          <div>
            <Label>Position</Label>
            <div className="flex gap-2 flex-wrap mt-1.5">
              {POSITIONS.map(p => (
                <Button key={p.value} size="sm" variant={position === p.value ? "default" : "outline"} onClick={() => { setPosition(p.value); setAngle(angleAfterPositionChange(angle, p.value)); setResult(null); }}>
                  {p.label}
                </Button>
              ))}
            </div>
          </div>

          {mode === "text" ? (
            <div className="flex flex-wrap gap-4">
              <div className="flex-1 min-w-[10rem]">
                <Label htmlFor="wm-size">Size: {size} pt (long text shrinks to fit)</Label>
                <input id="wm-size" type="range" min="12" max="160" value={size} onChange={e => { setSize(Number(e.target.value)); setResult(null); }} className="w-full mt-1.5" />
              </div>
              <div>
                <Label htmlFor="wm-color">Colour</Label>
                <input id="wm-color" type="color" value={color} onChange={e => { setColor(e.target.value); setResult(null); }} className="block h-9 w-14 mt-1.5 rounded border bg-transparent" />
              </div>
            </div>
          ) : (
            <div>
              <Label htmlFor="wm-scale">Logo width: {logoScale}% of the page</Label>
              <input id="wm-scale" type="range" min="5" max="80" value={logoScale} onChange={e => { setLogoScale(Number(e.target.value)); setResult(null); }} className="w-full mt-1.5" />
            </div>
          )}

          <div>
            <Label htmlFor="wm-angle">Angle: {angle}°</Label>
            <input id="wm-angle" type="range" min="0" max="90" value={angle} onChange={e => { setAngle(Number(e.target.value)); setResult(null); }} className="w-full mt-1.5" />
            <p className="text-xs text-muted-foreground mt-1">Centre and tiled start at 45°, corners start straight (0°). An angle you set yourself is kept when you change the position.</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="wm-pages">Pages (leave empty for every page)</Label>
            <Input id="wm-pages" value={pageRange} placeholder="e.g. 2-5, 8" aria-invalid={!!pagesError} onChange={e => { setPageRange(e.target.value); setResult(null); }} />
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={skipFirst} onChange={e => { setSkipFirst(e.target.checked); setResult(null); }} /> Skip the first page (for a cover)
            </label>
            {pagesError && <p role="alert" className="text-xs text-destructive mt-1">{pagesError}</p>}
          </div>

          <div>
            <Label>Layer</Label>
            <div className="flex gap-2 flex-wrap mt-1.5" role="group" aria-label="Layer">
              <Button size="sm" variant={layer === "above" ? "default" : "outline"} aria-pressed={layer === "above"} onClick={() => { setLayer("above"); setResult(null); }}>On top of the page</Button>
              <Button size="sm" variant={layer === "behind" ? "default" : "outline"} aria-pressed={layer === "behind"} onClick={() => { setLayer("behind"); setResult(null); }}>Behind page content</Button>
            </div>
            {layer === "behind" && <p className="text-xs text-muted-foreground mt-1">The page's own text stays fully dark over the watermark. Pages that are a full-page scan or have a solid background colour will hide it, because the page paints over it. Use On top for those.</p>}
          </div>

          <div>
            <Label>Opacity: {opacity}%</Label>
            <input
              type="range"
              min="5"
              max="100"
              value={opacity}
              onChange={e => { setOpacity(Number(e.target.value)); setResult(null); }}
              className="w-full mt-1.5"
            />
          </div>
    </div>
  );

  return (
    <ToolContainer>
      <BatchModeToggle batch={batchMode} onChange={setBatchMode} />
      {batchMode ? (
        <BatchRunner
          accept=".pdf" noun="PDF" zipName="watermarked-pdfs.zip" runLabel="Add watermark"
          check={settingsProblem} disableOnCheck
          process={async (f) => ({ blob: await stamp(f), fileName: `${stemOfPdfName(f.name)}-watermarked.pdf` })}
        >
          {settingsBlock}
        </BatchRunner>
      ) : !file ? (
        <FileDropZone gateAtDownload accept=".pdf" onFiles={handleFiles} label="Drop a PDF file" description="Add a text or image watermark to every page" />
      ) : (
        <div className="space-y-4">
          <div className="p-4 rounded-lg border bg-card"><p className="text-sm font-medium">{file.name}</p></div>

          {settingsBlock}

          <div className="flex gap-3">
            {processing ? <ProcessingState /> : result ? (
              <><DownloadButton blob={result} fileName={`${stemOfPdfName(file.name)}-watermarked.pdf`} /><Button variant="outline" onClick={() => { setFile(null); setResult(null); }}>New</Button></>
            ) : (
              <><Button onClick={addWatermark} disabled={!!settingsProblem()}>Add Watermark</Button><Button variant="outline" onClick={() => setFile(null)}>Reset</Button></>
            )}
          </div>
          {result && <ResultPreview blob={result} mimeType="application/pdf" />}
        </div>
      )}
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
